import React from "react";
import { Link } from "react-router-dom";
import { CABANAS } from "@/data/cabanas";
import { Flame, Trees, ShieldCheck, Coffee, Heart, Sparkles, ArrowRight } from "lucide-react";

export default function Home() {
    return (
        <div className="bg-cdb-cream">
            {/* HERO */}
            <section className="relative h-[90vh] min-h-[600px] w-full overflow-hidden">
                <img
                    src="https://images.pexels.com/photos/27111536/pexels-photo-27111536.jpeg"
                    alt="Mata Atlântica"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 hero-overlay" />
                <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-8 flex flex-col justify-end pb-20 sm:pb-28">
                    <div className="max-w-3xl reveal">
                        <span
                            data-testid="hero-badge"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cdb-cream/15 backdrop-blur text-cdb-cream text-sm font-bold mb-6 border border-cdb-cream/20"
                        >
                            <Sparkles className="w-4 h-4" /> Refúgio na Mata Atlântica
                        </span>
                        <h1
                            data-testid="hero-title"
                            className="text-4xl sm:text-5xl lg:text-7xl font-bold text-cdb-cream tracking-tight leading-[1.05] mb-6"
                        >
                            Onde a natureza<br />
                            abraça o amor.
                        </h1>
                        <p className="text-base sm:text-lg text-cdb-cream/90 max-w-xl mb-8 leading-relaxed">
                            Três cabanas exclusivas em Barra de Guaratiba — Rio de Janeiro. Piscina aquecida 24h,
                            vista deslumbrante e privacidade total para casais que amam a simplicidade da natureza.
                        </p>
                        <div className="flex flex-wrap gap-4">
                            <Link
                                to="/reservas"
                                data-testid="hero-cta-reservar"
                                className="inline-flex items-center gap-2 bg-cdb-gold hover:bg-cdb-goldDark text-white rounded-full px-8 py-4 font-bold transition-all shadow-lg hover:shadow-xl hover:scale-[1.02]"
                            >
                                Fazer uma reserva <ArrowRight className="w-5 h-5" />
                            </Link>
                            <Link
                                to="/cabanas"
                                data-testid="hero-cta-conhecer"
                                className="inline-flex items-center gap-2 bg-cdb-cream/10 backdrop-blur hover:bg-cdb-cream/20 text-cdb-cream rounded-full px-8 py-4 font-bold transition-all border border-cdb-cream/30"
                            >
                                Conhecer as cabanas
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* SOBRE */}
            <section className="py-24 sm:py-32 leaf-pattern">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-12 gap-12 items-center">
                    <div className="md:col-span-5">
                        <span className="text-cdb-gold text-sm font-bold uppercase tracking-widest">
                            Sobre nós
                        </span>
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-cdb-forest mt-3 mb-6 leading-tight">
                            Um cantinho de descanso entre montanhas e mar.
                        </h2>
                        <p className="text-cdb-earth leading-relaxed mb-4">
                            Tudo na Cabana da Barra foi carinhosamente pensado para atender casais que amam
                            simplicidade, conforto e a privacidade que a natureza oferece.
                        </p>
                        <p className="text-cdb-earth leading-relaxed">
                            Em meio à Mata Atlântica, em Barra de Guaratiba, oferecemos experiências exclusivas com
                            piscina aquecida, automação por voz e detalhes que tornam cada noite inesquecível.
                        </p>
                    </div>
                    <div className="md:col-span-7 grid grid-cols-2 gap-4">
                        <img
                            src="/cabanas/cabana01/capa.webp"
                            alt="Cabana da Barra 01"
                            className="rounded-3xl aspect-[3/4] object-cover w-full"
                        />
                        <div className="space-y-4">
                            <img
                                src="/cabanas/aquario/capa.webp"
                                alt="Cabana Aquário"
                                className="rounded-3xl aspect-square object-cover w-full"
                            />
                            <img
                                src="/cabanas/cabana02/foto_01.avif"
                                alt="Cabana da Barra 02"
                                className="rounded-3xl aspect-square object-cover w-full"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* AMENIDADES */}
            <section className="py-24 sm:py-32 bg-cdb-sand/40">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-center max-w-2xl mx-auto mb-16">
                        <span className="text-cdb-gold text-sm font-bold uppercase tracking-widest">
                            Experiência
                        </span>
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-cdb-forest mt-3 leading-tight">
                            Pensado nos mínimos detalhes para o seu descanso.
                        </h2>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                        {[
                            { icon: Flame, t: "Piscina aquecida 24h", d: "Mergulho relaxante a qualquer hora." },
                            { icon: Trees, t: "Mata Atlântica", d: "Vista deslumbrante e som da floresta." },
                            { icon: ShieldCheck, t: "Privacidade total", d: "Cabanas isoladas e exclusivas." },
                            { icon: Coffee, t: "Café e jantar incluso", d: "Refeições preparadas para você." },
                        ].map(({ icon: Icon, t, d }) => (
                            <div
                                key={t}
                                className="bg-white rounded-3xl p-6 sm:p-8 border border-cdb-sand hover:-translate-y-1 transition-transform duration-300"
                            >
                                <div className="w-12 h-12 rounded-2xl bg-cdb-forest/10 text-cdb-forest flex items-center justify-center mb-4">
                                    <Icon className="w-6 h-6" />
                                </div>
                                <h4 className="text-base sm:text-lg font-bold text-cdb-forest mb-2">{t}</h4>
                                <p className="text-sm text-cdb-earth/80 leading-relaxed">{d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CABANAS */}
            <section className="py-24 sm:py-32">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-4">
                        <div>
                            <span className="text-cdb-gold text-sm font-bold uppercase tracking-widest">
                                Nossas cabanas
                            </span>
                            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-cdb-forest mt-3 leading-tight">
                                Escolha o seu refúgio.
                            </h2>
                        </div>
                        <Link
                            to="/cabanas"
                            data-testid="home-link-cabanas"
                            className="inline-flex items-center gap-2 text-cdb-forest font-bold hover:text-cdb-gold"
                        >
                            Ver todas <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
                        {CABANAS.map((c) => (
                            <Link
                                key={c.id}
                                to={`/cabanas/${c.slug}`}
                                data-testid={`home-cabana-card-${c.id}`}
                                className="group bg-white rounded-3xl overflow-hidden border border-cdb-sand hover:shadow-2xl transition-all duration-500 hover:-translate-y-1"
                            >
                                <div className="aspect-[4/3] overflow-hidden">
                                    <img
                                        src={c.capa}
                                        alt={c.nome}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                </div>
                                <div className="p-6">
                                    <h3 className="text-xl font-bold text-cdb-forest mb-2">{c.nome}</h3>
                                    <p className="text-sm text-cdb-earth/80 mb-4 flex items-center gap-2">
                                        <Heart className="w-4 h-4 text-cdb-gold" /> {c.destaque}
                                    </p>
                                    <div className="flex items-baseline justify-between pt-4 border-t border-cdb-sand">
                                        <div>
                                            <p className="text-xs text-cdb-earth/60">A partir de</p>
                                            <p className="text-2xl font-bold text-cdb-forest">
                                                R$ {c.diaria.toLocaleString("pt-BR")}
                                                <span className="text-sm font-normal text-cdb-earth/60"> /noite</span>
                                            </p>
                                        </div>
                                        <span className="text-cdb-gold font-bold text-sm group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                                            Ver detalhes <ArrowRight className="w-4 h-4" />
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 sm:py-32">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="relative rounded-[2rem] overflow-hidden bg-cdb-forest text-cdb-cream p-10 sm:p-16">
                        <div className="absolute inset-0 opacity-20">
                            <img
                                src="https://images.pexels.com/photos/27111536/pexels-photo-27111536.jpeg"
                                alt=""
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="relative max-w-2xl">
                            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
                                Sua próxima escapada começa aqui.
                            </h2>
                            <p className="text-cdb-cream/90 mb-8 text-base sm:text-lg leading-relaxed">
                                Reserve agora e prepare-se para vivenciar uma experiência inesquecível em meio à
                                Mata Atlântica.
                            </p>
                            <Link
                                to="/reservas"
                                data-testid="cta-final-reservar"
                                className="inline-flex items-center gap-2 bg-cdb-gold hover:bg-cdb-goldDark text-white rounded-full px-8 py-4 font-bold transition-all shadow-lg hover:shadow-xl"
                            >
                                Solicitar reserva <ArrowRight className="w-5 h-5" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
